import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, Edit, Trash2, RefreshCw, Settings, FileText, Info, CheckCircle, XCircle,
  ChevronUp, ChevronDown, Search, X, Save, Eye, Building2, Type, Users, Calendar,
  Hash, ListOrdered, SlidersHorizontal, AlertTriangle, Layers
} from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

/* -------------------------------------------------------------------------- */
/*  Constantes & helpers                                                      */
/* -------------------------------------------------------------------------- */

const DEFAULT_ORDRE = ['prefixe', 'code_filiale', 'direct_indirect', 'annee', 'sequentiel'];

// Chaque élément du matricule garde la même couleur partout (tableau, aperçu, ordre)
const TOKENS = {
  prefixe: {
    label: 'Préfixe', icon: Type,
    chip: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    hero: 'bg-indigo-400/25 border-indigo-300',
    dot: 'bg-indigo-500', tile: 'bg-indigo-50 text-indigo-600'
  },
  code_filiale: {
    label: 'Code filiale', icon: Building2,
    chip: 'bg-blue-50 text-blue-700 border-blue-200',
    hero: 'bg-sky-400/25 border-sky-300',
    dot: 'bg-blue-500', tile: 'bg-blue-50 text-blue-600'
  },
  direct_indirect: {
    label: 'Direct / Indirect', icon: Users,
    chip: 'bg-amber-50 text-amber-700 border-amber-200',
    hero: 'bg-amber-400/25 border-amber-300',
    dot: 'bg-amber-500', tile: 'bg-amber-50 text-amber-600'
  },
  annee: {
    label: 'Année', icon: Calendar,
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    hero: 'bg-emerald-400/25 border-emerald-300',
    dot: 'bg-emerald-500', tile: 'bg-emerald-50 text-emerald-600'
  },
  sequentiel: {
    label: 'Séquentiel', icon: Hash,
    chip: 'bg-gray-100 text-gray-700 border-gray-300',
    hero: 'bg-white/20 border-white/60',
    dot: 'bg-gray-500', tile: 'bg-gray-100 text-gray-600'
  }
};

const TABS = [
  { id: 'general', label: 'Général', icon: Building2, fields: ['code_filiale', 'prefixe'] },
  { id: 'format', label: 'Format', icon: SlidersHorizontal, fields: ['longueur_sequentiel'] },
  { id: 'ordre', label: 'Ordre des éléments', icon: ListOrdered, fields: [] }
];

const STATUS_FILTERS = [
  { id: 'tous', label: 'Toutes' },
  { id: 'actif', label: 'Actives' },
  { id: 'inactif', label: 'Inactives' }
];

const toBool = (v) => v === true || v === 1 || v === '1';

// Accepte un tableau ou une chaîne "a,b,c" et complète avec les éléments manquants
const parseOrdre = (value) => {
  const list = Array.isArray(value)
    ? value
    : typeof value === 'string' && value ? value.split(',').map((s) => s.trim()) : [];
  const valid = [...new Set(list.filter((k) => DEFAULT_ORDRE.includes(k)))];
  return [...valid, ...DEFAULT_ORDRE.filter((k) => !valid.includes(k))];
};

const emptyForm = (filiale_id = '') => ({
  filiale_id,
  prefixe: 'PD',
  code_filiale: '',
  inclure_direct_indirect: true,
  separateur: '',
  inclure_annee: true,
  format_annee: 'AAAA',
  prefixe_annee: '',
  longueur_sequentiel: 6,
  actif: true,
  ordre_elements: [...DEFAULT_ORDRE]
});

const fromRecord = (n) => ({
  filiale_id: n.filiale_id,
  prefixe: n.prefixe || '',
  code_filiale: n.code_filiale || '',
  inclure_direct_indirect: toBool(n.inclure_direct_indirect),
  separateur: n.separateur || '',
  inclure_annee: toBool(n.inclure_annee),
  format_annee: n.format_annee || 'AAAA',
  prefixe_annee: n.prefixe_annee || '',
  longueur_sequentiel: Number(n.longueur_sequentiel) || 6,
  actif: toBool(n.actif),
  ordre_elements: parseOrdre(n.ordre_elements)
});

// mode 'example' : valeurs réelles (PDMCBD2026000001) — mode 'pattern' : gabarit ([D/I], [AAAA], 000000)
const buildValues = (c, mode = 'example') => {
  const year = new Date().getFullYear();
  const yy = c.format_annee === 'AA' ? String(year).slice(-2) : String(year);
  const len = Math.max(Number(c.longueur_sequentiel) || 6, 1);
  if (mode === 'pattern') {
    return {
      prefixe: c.prefixe,
      code_filiale: c.code_filiale,
      direct_indirect: c.inclure_direct_indirect ? '[D/I]' : '',
      annee: c.inclure_annee ? `[${c.format_annee}]` : '',
      sequentiel: '0'.repeat(len)
    };
  }
  return {
    prefixe: c.prefixe,
    code_filiale: c.code_filiale,
    direct_indirect: c.inclure_direct_indirect ? 'D' : '',
    annee: c.inclure_annee ? `${c.prefixe_annee || ''}${yy}` : '',
    sequentiel: '0'.repeat(len - 1) + '1'
  };
};

const buildSegments = (c, mode = 'example') => {
  const values = buildValues(c, mode);
  return parseOrdre(c.ordre_elements)
    .map((key) => ({ key, text: values[key] }))
    .filter((s) => s.text);
};

const inputCls = (error) =>
  `w-full px-3 py-2 text-sm border rounded-lg bg-white placeholder-gray-400 transition focus:outline-none focus:ring-2 ${
    error
      ? 'border-red-300 focus:ring-red-200 focus:border-red-400'
      : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
  }`;

/* -------------------------------------------------------------------------- */
/*  Petits composants                                                         */
/* -------------------------------------------------------------------------- */

const Overlay = ({ children }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    {children}
  </div>
);

const Field = ({ label, required, hint, error, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}{required && <span className="text-red-500"> *</span>}
    </label>
    {children}
    {error ? (
      <p className="mt-1 text-xs text-red-600">{error}</p>
    ) : hint ? (
      <p className="mt-1 text-xs text-gray-500">{hint}</p>
    ) : null}
  </div>
);

const Toggle = ({ checked, onChange, label, description }) => (
  <div
    onClick={() => onChange(!checked)}
    className="flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-gray-200 p-4 transition hover:border-blue-300 hover:bg-blue-50/40"
  >
    <div>
      <div className="text-sm font-medium text-gray-900">{label}</div>
      {description && <div className="mt-0.5 text-xs text-gray-500">{description}</div>}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
        checked ? 'bg-blue-600' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  </div>
);

const SegmentChips = ({ segments, separateur }) => (
  <div className="flex flex-wrap items-center gap-1 font-mono text-xs">
    {segments.map((s, i) => (
      <React.Fragment key={s.key}>
        {i > 0 && separateur && <span className="text-gray-400">{separateur}</span>}
        <span className={`rounded border px-1.5 py-0.5 ${TOKENS[s.key].chip}`}>{s.text}</span>
      </React.Fragment>
    ))}
  </div>
);

// Aperçu en direct : visible dans tous les onglets de la modale
const PreviewBanner = ({ config }) => {
  const segments = buildSegments(config, 'example');
  const plain = segments.map((s) => s.text).join(config.separateur || '');
  return (
    <div className="rounded-xl bg-blue-900 p-4 text-white shadow-sm">
      <div className="flex items-center justify-between text-xs text-blue-200">
        <span className="inline-flex items-center gap-1.5 font-medium">
          <Eye className="h-3.5 w-3.5" /> Aperçu du matricule
        </span>
        <span>{plain.length} caractères</span>
      </div>
      <div className="mt-3 flex flex-wrap items-start gap-x-1 gap-y-2 font-mono text-xl font-semibold sm:text-2xl">
        {segments.length === 0 ? (
          <span className="text-blue-300">Aucun élément à afficher</span>
        ) : (
          segments.map((s, i) => (
            <React.Fragment key={s.key}>
              {i > 0 && config.separateur && <span className="pt-1 text-blue-300">{config.separateur}</span>}
              <span className="flex flex-col items-center">
                <span className={`rounded-md border-b-2 px-2 py-1 ${TOKENS[s.key].hero}`}>{s.text}</span>
                <span className="mt-1 font-sans text-[11px] font-normal text-blue-200">{TOKENS[s.key].label}</span>
              </span>
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*  Vue principale                                                            */
/* -------------------------------------------------------------------------- */

const NomenclatureMatricule = () => {
  const { user } = useAuth();
  const { filialeId } = useFiliale();
  const isSuperAdmin = user && user.role === 'super_admin';

  const [nomenclatures, setNomenclatures] = useState([]);
  const [filiales, setFiliales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('tous');

  const [showModal, setShowModal] = useState(false);
  const [editingNomenclature, setEditingNomenclature] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [activeTab, setActiveTab] = useState('general');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [toast, setToast] = useState(null);

  const notify = (type, message) => setToast({ type, message });

  useEffect(() => {
    fetchNomenclatures();
    fetchFiliales();
  }, [filialeId]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4500);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!showModal) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showModal]);

  /* ----------------------------- Chargement ------------------------------ */

  const fetchNomenclatures = async () => {
    try {
      setLoading(true);
      const response = await http.get('/api/nomenclature-matricule');
      let data = response.data || [];

      // Filtrer par filiale si une filiale est sélectionnée (pour tous les utilisateurs)
      if (filialeId && filialeId !== 'all') {
        data = data.filter((n) => n.filiale_id === parseInt(filialeId));
      }
      setNomenclatures(data);
    } catch (error) {
      console.error('Erreur chargement nomenclatures:', error);
      notify('error', 'Impossible de charger les nomenclatures');
    } finally {
      setLoading(false);
    }
  };

  const fetchFiliales = async () => {
    try {
      const response = await http.get('/api/filiales?all=true');
      setFiliales(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement filiales:', error);
    }
  };

  /* ------------------------------ Modale ---------------------------------- */

  const openModal = (nomenclature, data) => {
    setEditingNomenclature(nomenclature);
    setFormData(data);
    setErrors({});
    setActiveTab('general');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingNomenclature(null);
    setErrors({});
  };

  const handleCreate = () => {
    if (filialeId === 'all' || !filialeId) {
      notify('error', 'Sélectionnez une filiale dans le Périmètre avant de créer une nomenclature.');
      return;
    }
    openModal(null, emptyForm(parseInt(filialeId)));
  };

  const handleEdit = (nomenclature) => openModal(nomenclature, fromRecord(nomenclature));

  const setField = (patch) => {
    setFormData((prev) => ({ ...prev, ...patch }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((k) => delete next[k]);
      return next;
    });
  };

  const moveElement = (index, direction) => {
    setFormData((prev) => {
      const next = [...prev.ordre_elements];
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...prev, ordre_elements: next };
    });
  };

  const isElementEnabled = (key) => {
    if (key === 'direct_indirect') return formData.inclure_direct_indirect;
    if (key === 'annee') return formData.inclure_annee;
    return true;
  };

  const validate = () => {
    const found = {};
    if (!formData.code_filiale.trim()) found.code_filiale = 'Le code filiale est obligatoire';
    if (!formData.prefixe.trim()) found.prefixe = 'Le préfixe est obligatoire';
    const len = Number(formData.longueur_sequentiel);
    if (!Number.isInteger(len) || len < 4 || len > 10) {
      found.longueur_sequentiel = 'La longueur doit être comprise entre 4 et 10';
    }
    return found;
  };

  const tabHasError = (tab) => tab.fields.some((f) => errors[f]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const found = validate();
    if (Object.keys(found).length > 0) {
      setErrors(found);
      const firstTab = TABS.find((t) => t.fields.some((f) => found[f]));
      if (firstTab) setActiveTab(firstTab.id);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        code_filiale: formData.code_filiale.trim(),
        prefixe: formData.prefixe.trim()
      };
      if (editingNomenclature) {
        await http.put(`/api/nomenclature-matricule/${editingNomenclature.id}`, payload);
      } else {
        await http.post('/api/nomenclature-matricule', payload);
      }
      notify('success', editingNomenclature ? 'Nomenclature mise à jour' : 'Nomenclature créée');
      closeModal();
      fetchNomenclatures();
    } catch (error) {
      console.error('Erreur sauvegarde nomenclature:', error);
      notify('error', error.response?.data?.error || error.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------- Actions sur les lignes ----------------------- */

  const handleDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target) return;
    try {
      await http.delete(`/api/nomenclature-matricule/${target.id}`);
      notify('success', 'Nomenclature supprimée');
      fetchNomenclatures();
    } catch (error) {
      console.error('Erreur suppression nomenclature:', error);
      notify('error', 'Erreur lors de la suppression');
    }
  };

  const handleGenerateMatricule = async (targetFilialeId) => {
    try {
      const response = await http.post('/api/nomenclature-matricule/generer', { filiale_id: targetFilialeId });
      notify('success', `Matricule généré : ${response.data.matricule}`);
      fetchNomenclatures();
    } catch (error) {
      console.error('Erreur génération matricule:', error);
      notify('error', 'Erreur lors de la génération du matricule');
    }
  };

  const handleResetSequentiel = async () => {
    const value = parseInt(resetTarget.value, 10);
    if (Number.isNaN(value) || value < 0) {
      setResetTarget({ ...resetTarget, error: 'Saisissez un nombre entier positif ou nul' });
      return;
    }
    const { id } = resetTarget;
    setResetTarget(null);
    try {
      await http.post(`/api/nomenclature-matricule/${id}/reinitialiser`, { nouveau_sequentiel: value });
      notify('success', 'Séquentiel mis à jour');
      fetchNomenclatures();
    } catch (error) {
      console.error('Erreur réinitialisation séquentiel:', error);
      notify('error', 'Erreur lors de la réinitialisation');
    }
  };

  /* ------------------------------ Données dérivées ------------------------ */

  const stats = useMemo(() => ({
    total: nomenclatures.length,
    actives: nomenclatures.filter((n) => toBool(n.actif)).length,
    inactives: nomenclatures.filter((n) => !toBool(n.actif)).length,
    filiales: new Set(nomenclatures.map((n) => n.filiale_id)).size
  }), [nomenclatures]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return nomenclatures.filter((n) => {
      if (statusFilter === 'actif' && !toBool(n.actif)) return false;
      if (statusFilter === 'inactif' && toBool(n.actif)) return false;
      if (!q) return true;
      return [n.filiale_libelle, n.pays, n.code_filiale, n.prefixe, n.exemple]
        .some((v) => String(v ?? '').toLowerCase().includes(q));
    });
  }, [nomenclatures, search, statusFilter]);

  const filialeInfo = filiales.find((f) => Number(f.id) === Number(formData.filiale_id));
  const filialeNom =
    filialeInfo?.libelle ?? filialeInfo?.nom ?? editingNomenclature?.filiale_libelle ?? `Filiale #${formData.filiale_id}`;
  const filialePays = filialeInfo?.pays ?? editingNomenclature?.pays;

  const kpis = [
    { label: 'Nomenclatures', value: stats.total, icon: Layers, tile: 'bg-blue-50 text-blue-600' },
    { label: 'Actives', value: stats.actives, icon: CheckCircle, tile: 'bg-green-50 text-green-600' },
    { label: 'Inactives', value: stats.inactives, icon: XCircle, tile: 'bg-red-50 text-red-600' },
    { label: 'Filiales couvertes', value: stats.filiales, icon: Building2, tile: 'bg-indigo-50 text-indigo-600' }
  ];

  const colCount = isSuperAdmin ? 6 : 5;

  /* -------------------------------- Rendu --------------------------------- */

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Nomenclature Matricule"
        description="Configuration des formats de matricule par filiale"
        icon={Settings}
        actions={[
          { label: 'Actualiser', icon: RefreshCw, onClick: fetchNomenclatures, primary: false },
          { label: 'Nouvelle nomenclature', icon: Plus, onClick: handleCreate, primary: true }
        ]}
      />

      <div className="px-6 py-8 space-y-6">
        {/* Indicateurs */}
        <div className="grid grid-cols-2 divide-x divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm lg:grid-cols-4 lg:divide-y-0">
          {kpis.map(({ label, value, icon: Icon, tile }) => (
            <div key={label} className="flex items-center gap-4 p-5">
              <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${tile}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <div className="text-2xl font-semibold text-gray-900">{value}</div>
                <div className="text-sm text-gray-500">{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Liste */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher une filiale ou un format"
                className={`${inputCls(false)} pl-9`}
              />
            </div>
            <div className="inline-flex rounded-lg bg-gray-100 p-1">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setStatusFilter(f.id)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                    statusFilter === f.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex h-48 items-center justify-center gap-2 text-sm text-gray-500">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Chargement des nomenclatures...
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {isSuperAdmin && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filiale</th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Format</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exemple</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dernier séquentiel</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={colCount} className="px-6 py-14 text-center">
                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 text-gray-400">
                          <Settings className="h-6 w-6" />
                        </div>
                        <p className="mt-3 text-sm font-medium text-gray-900">
                          {nomenclatures.length === 0 ? 'Aucune nomenclature configurée' : 'Aucun résultat pour cette recherche'}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                          {nomenclatures.length === 0
                            ? 'Créez le format de matricule de la filiale pour commencer.'
                            : 'Modifiez la recherche ou le filtre de statut.'}
                        </p>
                        {nomenclatures.length === 0 && (
                          <button
                            type="button"
                            onClick={handleCreate}
                            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                          >
                            <Plus className="h-4 w-4" />
                            Nouvelle nomenclature
                          </button>
                        )}
                      </td>
                    </tr>
                  ) : (
                    filtered.map((n) => {
                      const config = fromRecord(n);
                      const capacity = 10 ** config.longueur_sequentiel - 1;
                      const used = Number(n.dernier_sequentiel) || 0;
                      const pct = Math.min(100, Math.round((used / capacity) * 100));
                      const barColor = pct >= 95 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-blue-500';
                      return (
                        <tr key={n.id} className="hover:bg-gray-50">
                          {isSuperAdmin && (
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{n.filiale_libelle}</div>
                              <div className="text-sm text-gray-500">{n.pays}</div>
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <SegmentChips segments={buildSegments(config, 'pattern')} separateur={config.separateur} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="inline-block font-mono text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                              {n.exemple}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">#{used.toLocaleString('fr-FR')}</div>
                            <div
                              className="mt-1.5 h-1.5 w-28 overflow-hidden rounded-full bg-gray-100"
                              title={`${pct}% de la capacité (${capacity.toLocaleString('fr-FR')} matricules)`}
                            >
                              <div className={`h-full rounded-full ${barColor}`} style={{ width: `${used > 0 ? Math.max(pct, 2) : 0}%` }} />
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {toBool(n.actif) ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Actif
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <XCircle className="h-3 w-3 mr-1" />
                                Inactif
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end gap-1">
                              <button
                                onClick={() => handleGenerateMatricule(n.filiale_id)}
                                className="rounded-md p-1.5 text-green-600 hover:bg-green-50 hover:text-green-800"
                                title="Générer matricule"
                              >
                                <FileText className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => setResetTarget({ id: n.id, value: '0', error: '' })}
                                className="rounded-md p-1.5 text-orange-600 hover:bg-orange-50 hover:text-orange-800"
                                title="Réinitialiser séquentiel"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleEdit(n)}
                                className="rounded-md p-1.5 text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                                title="Modifier"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => setDeleteTarget(n)}
                                className="rounded-md p-1.5 text-red-600 hover:bg-red-50 hover:text-red-800"
                                title="Supprimer"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Légende */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-6 py-3 text-xs text-gray-500">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              {DEFAULT_ORDRE.map((key) => (
                <span key={key} className="inline-flex items-center gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${TOKENS[key].dot}`} />
                  {TOKENS[key].label}
                </span>
              ))}
            </div>
            <span>{filtered.length} sur {nomenclatures.length} nomenclature(s)</span>
          </div>
        </div>
      </div>

      {/* ------------------------- Modale à onglets ------------------------- */}
      {showModal && (
        <Overlay>
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 p-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <Settings className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {editingNomenclature ? 'Modifier la nomenclature' : 'Nouvelle nomenclature'}
                  </h2>
                  <p className="text-sm text-gray-500">Définissez la composition des matricules de la filiale</p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                title="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
              <div className="shrink-0 px-6 pt-5">
                <PreviewBanner config={formData} />
              </div>

              {/* Onglets */}
              <div role="tablist" className="mt-4 flex shrink-0 gap-1 border-b border-gray-200 px-6">
                {TABS.map((tab) => {
                  const Icon = tab.icon;
                  const active = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      id={`tab-${tab.id}`}
                      aria-selected={active}
                      aria-controls={`panel-${tab.id}`}
                      onClick={() => setActiveTab(tab.id)}
                      className={`-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200 ${
                        active
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                      {tabHasError(tab) && <span className="h-2 w-2 rounded-full bg-red-500" title="Champ à corriger" />}
                    </button>
                  );
                })}
              </div>

              {/* Contenu : tous les onglets restent montés pour que la validation voie chaque champ */}
              <div className="min-h-[22rem] flex-1 overflow-y-auto px-6 py-6">
                {/* Général */}
                <div
                  role="tabpanel"
                  id="panel-general"
                  aria-labelledby="tab-general"
                  className={activeTab === 'general' ? 'space-y-5' : 'hidden'}
                >
                  <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white text-blue-600 border border-gray-200">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{filialeNom}</div>
                      <div className="text-sm text-gray-500">{filialePays || 'Filiale du périmètre sélectionné'}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Code filiale" required error={errors.code_filiale} hint="Identifie la filiale dans le matricule">
                      <input
                        type="text"
                        value={formData.code_filiale}
                        onChange={(e) => setField({ code_filiale: e.target.value.toUpperCase() })}
                        className={`${inputCls(errors.code_filiale)} uppercase`}
                        placeholder="Ex: MCB, CI, CG"
                      />
                    </Field>
                    <Field label="Préfixe" required error={errors.prefixe} hint="Catégorie de personnel, en tête du matricule">
                      <input
                        type="text"
                        value={formData.prefixe}
                        onChange={(e) => setField({ prefixe: e.target.value.toUpperCase() })}
                        className={`${inputCls(errors.prefixe)} uppercase`}
                        placeholder="Ex: PD pour Personnel"
                      />
                    </Field>
                  </div>

                  <Toggle
                    checked={formData.actif}
                    onChange={(v) => setField({ actif: v })}
                    label="Nomenclature active"
                    description="Une nomenclature inactive n'est plus utilisée pour générer les matricules."
                  />
                </div>

                {/* Format */}
                <div
                  role="tabpanel"
                  id="panel-format"
                  aria-labelledby="tab-format"
                  className={activeTab === 'format' ? 'space-y-5' : 'hidden'}
                >
                  <Field label="Séparateur" hint="Inséré entre chaque élément. Laissez vide pour un matricule compact.">
                    <input
                      type="text"
                      maxLength={3}
                      value={formData.separateur}
                      onChange={(e) => setField({ separateur: e.target.value })}
                      className={`${inputCls(false)} sm:max-w-xs`}
                      placeholder="Ex: -, _, ou laisser vide"
                    />
                  </Field>

                  <Toggle
                    checked={formData.inclure_direct_indirect}
                    onChange={(v) => setField({ inclure_direct_indirect: v })}
                    label="Inclure Direct / Indirect"
                    description="Ajoute D (direct) ou I (indirect) selon le type de personnel."
                  />

                  <div className="space-y-4 rounded-lg border border-gray-200 p-4">
                    <Toggle
                      checked={formData.inclure_annee}
                      onChange={(v) => setField({ inclure_annee: v })}
                      label="Inclure l'année"
                      description="Ajoute l'année de recrutement dans le matricule."
                    />
                    {formData.inclure_annee && (
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field label="Format année">
                          <div className="inline-flex rounded-lg bg-gray-100 p-1">
                            {[
                              { id: 'AAAA', label: `AAAA (${new Date().getFullYear()})` },
                              { id: 'AA', label: `AA (${String(new Date().getFullYear()).slice(-2)})` }
                            ].map((opt) => (
                              <button
                                key={opt.id}
                                type="button"
                                onClick={() => setField({ format_annee: opt.id })}
                                className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                                  formData.format_annee === opt.id
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </Field>
                        <Field label="Préfixe année" hint="Texte placé juste avant l'année">
                          <input
                            type="text"
                            value={formData.prefixe_annee}
                            onChange={(e) => setField({ prefixe_annee: e.target.value })}
                            className={inputCls(false)}
                            placeholder="Ex: -, _, ou laisser vide"
                          />
                        </Field>
                      </div>
                    )}
                  </div>

                  <Field label="Longueur du séquentiel" required error={errors.longueur_sequentiel}>
                    <div className="flex items-center gap-4 rounded-lg border border-gray-200 p-4">
                      <input
                        type="range"
                        min="4"
                        max="10"
                        value={formData.longueur_sequentiel}
                        onChange={(e) => setField({ longueur_sequentiel: parseInt(e.target.value, 10) })}
                        className="h-2 flex-1 cursor-pointer accent-blue-600"
                      />
                      <div className="w-40 text-right">
                        <div className="font-mono text-sm font-semibold text-gray-900">
                          {'0'.repeat(formData.longueur_sequentiel - 1)}1
                        </div>
                        <div className="text-xs text-gray-500">
                          {(10 ** formData.longueur_sequentiel - 1).toLocaleString('fr-FR')} matricules max
                        </div>
                      </div>
                    </div>
                  </Field>
                </div>

                {/* Ordre */}
                <div
                  role="tabpanel"
                  id="panel-ordre"
                  aria-labelledby="tab-ordre"
                  className={activeTab === 'ordre' ? 'space-y-4' : 'hidden'}
                >
                  <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                    Réorganisez les éléments avec les flèches. L'aperçu se met à jour immédiatement ; les éléments
                    désactivés sont ignorés.
                  </div>

                  <div className="space-y-2">
                    {formData.ordre_elements.map((key, index) => {
                      const token = TOKENS[key];
                      const Icon = token.icon;
                      const enabled = isElementEnabled(key);
                      const value = buildValues(formData, 'example')[key];
                      return (
                        <div
                          key={key}
                          className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 ${
                            enabled ? 'border-gray-200 bg-white' : 'border-dashed border-gray-300 bg-gray-50 opacity-60'
                          }`}
                        >
                          <span className="w-5 text-center text-sm font-medium text-gray-400">{index + 1}</span>
                          <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${token.tile}`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900">{token.label}</div>
                            <div className="text-xs text-gray-500">
                              {enabled ? (
                                <span className="font-mono">{value || 'Valeur à renseigner'}</span>
                              ) : (
                                'Désactivé dans l\'onglet Format'
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => moveElement(index, 'up')}
                            disabled={index === 0}
                            title="Monter"
                            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveElement(index, 'down')}
                            disabled={index === formData.ordre_elements.length - 1}
                            title="Descendre"
                            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Pied de modale */}
              <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
                <div className="text-xs">
                  {Object.keys(errors).length > 0 ? (
                    <span className="inline-flex items-center gap-1.5 text-red-600">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Corrigez les champs signalés avant d'enregistrer
                    </span>
                  ) : (
                    <span className="text-gray-500">Les champs marqués d'un * sont obligatoires</span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                  >
                    {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {editingNomenclature ? 'Mettre à jour' : 'Créer la nomenclature'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </Overlay>
      )}

      {/* ------------------------ Confirmation suppression ------------------ */}
      {deleteTarget && (
        <Overlay>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Supprimer cette nomenclature ?</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Le format {deleteTarget.exemple ? <span className="font-mono">{deleteTarget.exemple}</span> : 'sélectionné'}
                  {' '}ne sera plus disponible pour générer des matricules. Cette action est définitive.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-sm font-medium text-white rounded-lg hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ------------------------ Réinitialisation séquentiel --------------- */}
      {resetTarget && (
        <Overlay>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-600">
                <RefreshCw className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Réinitialiser le séquentiel</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Le prochain matricule généré reprendra après la valeur saisie. Saisissez 0 pour repartir de zéro.
                </p>
                <div className="mt-4">
                  <Field label="Nouveau séquentiel" error={resetTarget.error}>
                    <input
                      type="number"
                      min="0"
                      autoFocus
                      value={resetTarget.value}
                      onChange={(e) => setResetTarget({ ...resetTarget, value: e.target.value, error: '' })}
                      className={inputCls(resetTarget.error)}
                    />
                  </Field>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setResetTarget(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleResetSequentiel}
                className="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded-lg hover:bg-blue-700"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ------------------------------- Notification ----------------------- */}
      {toast && (
        <div
          role="status"
          className={`fixed right-4 top-4 z-[60] flex max-w-sm items-start gap-3 rounded-lg border p-4 shadow-lg ${
            toast.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-900'
              : 'border-red-200 bg-red-50 text-red-900'
          }`}
        >
          {toast.type === 'success' ? (
            <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
          ) : (
            <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
          )}
          <p className="text-sm">{toast.message}</p>
          <button type="button" onClick={() => setToast(null)} className="text-current opacity-50 hover:opacity-100">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default NomenclatureMatricule;